import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../api";
import {
  Search,
  Sparkles,
  Monitor,
  Gamepad2,
  Trophy,
  Swords,
  Check,
  UserPlus,
  Send,
  X,
  Users,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";

export default function PlayersPage() {
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [myClan, setMyClan] = useState(null);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [selectedFreeAgent, setSelectedFreeAgent] = useState(null);
  const [offerTerms, setOfferTerms] = useState("");
  const [sendingOffer, setSendingOffer] = useState(false);

  // جلب اللاعبين مع البحث + Pagination + Tab
  useEffect(() => {
    let cancelled = false;

    const timer = setTimeout(async () => {
      setLoading(true);

      try {
        const r = await api.get("/users/search", {
          params: {
            q,
            page,
            limit: 50,
            tab,
          },
        });

        if (cancelled) return;

        const data = r.data || {};

        setUsers(data.items || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);

        // إذا الصفحة الحالية أكبر من عدد الصفحات بعد بحث/فلترة
        if (page > (data.pages || 1)) {
          setPage(1);
        }
      } catch (err) {
        if (cancelled) return;

        setUsers([]);
        setTotal(0);
        setPages(1);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q, page, tab]);

  // جلب بيانات كلان المستخدم للتأكد من أنه القائد
  useEffect(() => {
    if (!user?.clan_id) {
      setMyClan(null);
      return;
    }

    api
      .get(`/clans/${user.clan_id}`)
      .then((r) => setMyClan(r.data))
      .catch(() => setMyClan(null));
  }, [user?.clan_id]);

  const isClanLeader = !!(
    user?.clan_id &&
    myClan?.leader_id === user?.id
  );

  // الفلترة أصبحت من السيرفر
  const filteredUsers = useMemo(() => users, [users]);

  const changeTab = (newTab) => {
    setTab(newTab);
    setPage(1);
  };

  const openOfferModal = (freeAgent) => {
    setSelectedFreeAgent(freeAgent);
    setOfferTerms("");
    setOfferModalOpen(true);
  };

  const sendContractOffer = async () => {
    if (!isClanLeader || !selectedFreeAgent?.id || !user?.clan_id) return;

    setSendingOffer(true);

    try {
      await api.post(`/clans/${user.clan_id}/contract-offer`, {
        user_id: selectedFreeAgent.id,
        terms: offerTerms,
      });

      toast.success("تم إرسال عرض التعاقد بنجاح! 📨");

      setOfferModalOpen(false);
      setSelectedFreeAgent(null);
      setOfferTerms("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSendingOffer(false);
    }
  };

  return (
    <div className="space-y-8" dir="rtl">
      {/* ── Header ── */}
      <div>
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-gold-500/10 border border-gold-500/20 text-gold-500 text-xs font-bold mb-2">
          <Users size={14} /> مجتمع اللاعبين
        </div>

        <h1 className="font-display font-black text-3xl md:text-5xl text-white">
          قائمة اللاعبين
        </h1>

        <p className="text-white/50 mt-1.5 text-sm">
          استكشف أفضل اللاعبين المحترفين، ابحث بالمعرف أو اسم المستخدم،
          وتواصل مع اللاعبين الأحرار.
        </p>
      </div>

      {/* ── Search Input ── */}
      <div className="relative max-w-xl">
        <Search
          size={18}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40"
        />

        <input
          data-testid="search-players"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="ابحث عن لاعب باسم الحساب، @username..."
          className="w-full bg-[#111113]/90 border border-white/10 rounded-2xl pr-11 pl-4 py-3.5 outline-none focus:border-gold-500/50 text-sm text-white placeholder:text-white/30 transition shadow-inner"
        />
      </div>

      {/* ── Tabs Filter Buttons ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => changeTab("all")}
          className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
            tab === "all"
              ? "bg-gold-500 text-black shadow-[0_0_15px_rgba(255,204,0,0.3)] font-black"
              : "bg-[#111113]/90 border border-white/10 text-white/70 hover:bg-white/5 hover:text-white"
          }`}
        >
          الكل {tab === "all" ? `(${total})` : ""}
        </button>

        <button
          type="button"
          onClick={() => changeTab("in-clan")}
          className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
            tab === "in-clan"
              ? "bg-gold-500 text-black shadow-[0_0_15px_rgba(255,204,0,0.3)] font-black"
              : "bg-[#111113]/90 border border-white/10 text-white/70 hover:bg-white/5 hover:text-white"
          }`}
        >
          في كلان (In-Clan)
          {tab === "in-clan" ? ` (${total})` : ""}
        </button>

        <button
          type="button"
          onClick={() => changeTab("free")}
          className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
            tab === "free"
              ? "bg-emerald-500 text-black shadow-[0_0_15px_rgba(16,185,129,0.35)] font-black"
              : "bg-[#111113]/90 border border-white/10 text-white/70 hover:bg-white/5 hover:text-white"
          }`}
        >
          لاعبون أحرار 🟢 (Free Agents)
          {tab === "free" ? ` (${total})` : ""}
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center text-white/40 text-sm py-4">
          جاري تحميل اللاعبين...
        </div>
      )}

      {/* ── Players Grid ── */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {!loading &&
          filteredUsers.map((u) => {
            const isPlusMember = !!(
              u.is_plus ||
              u.is_personal_plus ||
              u.plan === "plus"
            );

            const isFreeAgent = !u.clan_id;
            const platform = (u.gaming_platform || "pc").toLowerCase();
            const hiddenBadges = u.hidden_achievements || [];
            const accent =
              (isPlusMember && u.accent_color) || "#FFCC00";

            return (
              <div
                key={u.id}
                data-testid={`player-${u.id}`}
                className={`relative bg-[#111113]/90 border rounded-3xl p-5 flex flex-col justify-between gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                  isPlusMember
                    ? "border-gold-500/40 bg-gradient-to-b from-gold-500/5 via-transparent to-transparent shadow-[0_0_20px_rgba(255,204,0,0.06)]"
                    : "border-white/10 hover:border-gold-500/30"
                }`}
              >
                {/* الجزء العلوي */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <Link
                      to={`/players/${u.id}`}
                      className="h-14 w-14 rounded-2xl border-2 grid place-items-center font-display font-black text-xl overflow-hidden flex-shrink-0 transition-transform hover:scale-105 shadow-md"
                      style={{
                        backgroundColor:
                          isPlusMember && u.avatar
                            ? "transparent"
                            : `${accent}15`,
                        borderColor: `${accent}40`,
                        color: accent,
                      }}
                    >
                      {isPlusMember && u.avatar ? (
                        <img
                          src={u.avatar}
                          alt={u.username}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        (u.username?.[0] || "?").toUpperCase()
                      )}
                    </Link>

                    <div className="min-w-0">
                      <div className="font-bold truncate flex items-center gap-1.5">
                        <Link
                          to={`/players/${u.id}`}
                          className="truncate hover:text-gold-400 transition text-white text-base"
                        >
                          {u.act || u.username}
                        </Link>

                        {isPlusMember && (
                          <Sparkles
                            size={13}
                            className="text-[#d4af37] shrink-0 drop-shadow-[0_0_8px_rgba(212,175,55,0.7)]"
                          />
                        )}
                      </div>

                      {/* Platform + status */}
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {platform === "ps5" && (
                          <span className="text-[10px] px-2.5 py-0.5 rounded-lg bg-[#003791]/30 text-[#0070d1] border border-[#003791]/50 font-bold flex items-center gap-1">
                            <Gamepad2 size={11} /> PS5
                          </span>
                        )}

                        {platform === "xbox" && (
                          <span className="text-[10px] px-2.5 py-0.5 rounded-lg bg-[#107c41]/30 text-[#107c41] border border-[#107c41]/50 font-bold flex items-center gap-1">
                            <Gamepad2 size={11} /> Xbox
                          </span>
                        )}

                        {(platform === "pc" || !platform) && (
                          <span className="text-[10px] px-2.5 py-0.5 rounded-lg bg-purple-500/15 text-purple-300 border border-purple-500/30 font-bold flex items-center gap-1">
                            <Monitor size={11} /> PC
                          </span>
                        )}

                        {isFreeAgent ? (
                          <span className="inline-flex items-center rounded-lg px-2.5 py-0.5 text-[10px] font-extrabold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                            حر / Free Agent
                          </span>
                        ) : (
                          <span className="text-[10px] px-2.5 py-0.5 rounded-lg bg-white/5 border border-white/10 text-white/50">
                            {u.role === "owner"
                              ? "مالك"
                              : u.role === "admin"
                              ? "منظم"
                              : "في كلان"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Points */}
                  <div className="flex flex-col items-end shrink-0 bg-black/40 px-3 py-1.5 rounded-xl border border-white/5">
                    <div className="text-gold-500 font-display font-black text-base">
                      {u.points || 0}
                    </div>

                    <div className="text-[9px] uppercase tracking-wider text-white/40">
                      PTS
                    </div>
                  </div>
                </div>

                {/* Stats + contract */}
                <div className="flex items-center justify-between border-t border-white/10 pt-3.5 mt-1">
                  <div className="flex items-center gap-2 text-xs text-white/60">
                    {!hiddenBadges.includes("wins") && (
                      <span
                        className="flex items-center gap-1 bg-black/40 px-2.5 py-1.5 rounded-xl border border-white/5"
                        title="انتصارات"
                      >
                        <Swords size={13} className="text-amber-400" />
                        <span className="font-bold text-white">
                          {u.wins || 0}
                        </span>
                      </span>
                    )}

                    {!hiddenBadges.includes("mvp") && (
                      <span
                        className="flex items-center gap-1 bg-black/40 px-2.5 py-1.5 rounded-xl border border-white/5"
                        title="نجم المباراة"
                      >
                        <Trophy size={13} className="text-yellow-400" />
                        <span className="font-bold text-white">
                          {u.mvp_count || 0}
                        </span>
                      </span>
                    )}

                    {!hiddenBadges.includes("attendance") && (
                      <span
                        className="flex items-center gap-1 bg-black/40 px-2.5 py-1.5 rounded-xl border border-white/5"
                        title="مرات التحضير"
                      >
                        <Check size={13} className="text-gray-300" />
                        <span className="font-bold text-white">
                          {u.attendances || 0}
                        </span>
                      </span>
                    )}
                  </div>

                  {isClanLeader && isFreeAgent && (
                    <button
                      type="button"
                      onClick={() => openOfferModal(u)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-black bg-emerald-500 text-black hover:bg-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.35)] hover:scale-105 transition-all duration-200"
                    >
                      <UserPlus size={14} /> إرسال عقد
                    </button>
                  )}
                </div>
              </div>
            );
          })}

        {!loading && filteredUsers.length === 0 && (
          <div className="col-span-full text-center text-white/40 py-16 bg-[#111113]/40 border border-white/5 rounded-3xl text-sm">
            لا توجد نتائج مطابقة للبحث
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {!loading && pages > 1 && (
        <div className="flex items-center justify-center gap-2 flex-wrap pt-4">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-4 py-2 rounded-xl bg-[#111113] border border-white/10 text-white/70 text-xs font-bold hover:border-gold-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            السابق
          </button>

          {Array.from({ length: pages }, (_, i) => i + 1).map(
            (pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                onClick={() => setPage(pageNumber)}
                className={`h-9 min-w-9 px-3 rounded-xl text-xs font-black transition ${
                  page === pageNumber
                    ? "bg-gold-500 text-black shadow-[0_0_12px_rgba(255,204,0,0.25)]"
                    : "bg-[#111113] border border-white/10 text-white/70 hover:border-gold-500/40"
                }`}
              >
                {pageNumber}
              </button>
            )
          )}

          <button
            type="button"
            onClick={() =>
              setPage((p) => Math.min(pages, p + 1))
            }
            disabled={page >= pages}
            className="px-4 py-2 rounded-xl bg-[#111113] border border-white/10 text-white/70 text-xs font-bold hover:border-gold-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            التالي
          </button>
        </div>
      )}

      {/* ── Contract Offer Modal ── */}
      {offerModalOpen && selectedFreeAgent && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200">
          <div className="bg-[#141417] border border-white/15 rounded-3xl p-6 md:p-8 w-full max-w-lg space-y-5 shadow-2xl relative">
            <button
              type="button"
              onClick={() => setOfferModalOpen(false)}
              className="absolute top-5 left-5 p-1.5 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition"
            >
              <X size={18} />
            </button>

            <div className="flex items-center gap-3 border-b border-white/10 pb-4">
              <div className="h-11 w-11 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 grid place-items-center">
                <UserPlus size={22} />
              </div>

              <div>
                <h2 className="font-display font-black text-xl text-white">
                  إرسال عقد تعاقد 📝
                </h2>

                <p className="text-xs text-white/50">
                  تقديم عرض انضمام رسمي إلى صفوف كلانك
                </p>
              </div>
            </div>

            <div className="bg-black/40 border border-white/10 rounded-2xl p-4 flex items-center justify-between">
              <div className="text-xs text-white/60 font-bold">
                اللاعب المستهدف:
              </div>

              <div className="font-bold text-gold-400 text-sm font-mono">
                {selectedFreeAgent.act ||
                  selectedFreeAgent.username}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-white/70 block">
                شروط وتفاصيل العقد (اختياري)
              </label>

              <textarea
                value={offerTerms}
                onChange={(e) => setOfferTerms(e.target.value)}
                rows={4}
                placeholder="أدخل الشروط، المكافآت، أو رسالة ترحيبية للاعب..."
                className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 outline-none focus:border-emerald-500/60 resize-none text-sm text-white placeholder:text-white/30 transition"
              />
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setOfferModalOpen(false)}
                className="px-4 py-2.5 rounded-xl hover:bg-white/5 text-xs text-white/70 font-bold transition"
                disabled={sendingOffer}
              >
                إلغاء
              </button>

              <button
                type="button"
                onClick={sendContractOffer}
                disabled={sendingOffer}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-black font-black hover:bg-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.4)] disabled:opacity-60 text-xs transition"
              >
                {sendingOffer ? (
                  "جاري الإرسال..."
                ) : (
                  <>
                    <Send size={14} /> إرسال العقد الرسمي
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}